(function () {
  'use strict';

  function shouldPreview(state) {
    return Boolean(state.visible && !state.hidden && !state.reducedMotion && !state.saveData && !state.dialogOpen && !state.failed);
  }

  function playbackPosition(current, length) {
    const duration = Number.isFinite(length) && length > 0 ? length : 0;
    const time = Number.isFinite(current) ? Math.max(0, Math.min(current, duration)) : 0;
    return { time, duration, remaining: duration - time, progress: duration ? time / duration * 100 : 0 };
  }

  function recordingAt(recordings, index) {
    return Array.isArray(recordings) && Number.isInteger(index) ? recordings[index] || null : null;
  }

  function trackStops(offsets, width, viewport) {
    const max = Math.max(0, width - viewport);
    return offsets.length ? [...new Set(offsets.map(left => Math.max(0, Math.min(left - offsets[0], max))))] : [0];
  }

  function trackDestination(stops, current, key) {
    if (key === 'ArrowRight') return stops.find(left => left > current + 2) ?? stops.at(-1);
    if (key === 'ArrowLeft') return stops.slice().reverse().find(left => left < current - 2) ?? 0;
    if (key === 'Home') return 0;
    if (key === 'End') return stops.at(-1);
    return stops.reduce((best, left) => Math.abs(current - left) <= Math.abs(current - best) ? left : best, 0);
  }

  function clock(seconds) {
    const whole = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
    return Math.floor(whole / 60) + ':' + String(whole % 60).padStart(2, '0');
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { shouldPreview, playbackPosition, recordingAt, trackStops, trackDestination, clock };
  }
  if (typeof document === 'undefined') return;

  const config = window.LE || {};
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection;
  const previewUpdates = [];
  const dialogOpeners = new WeakMap();
  const boundDialogs = new WeakSet();
  const audioPlayers = new WeakMap();
  const svgNamespace = 'http://www.w3.org/2000/svg';
  const iconPaths = { play: 'm9 5 11 7-11 7V5Z', pause: 'M8 5v14M16 5v14', close: 'm6 6 12 12M6 18 18 6' };

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function icon(name, size) {
    const svg = document.createElementNS(svgNamespace, 'svg');
    const attributes = { width: size || 22, height: size || 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '1.5', 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'aria-hidden': 'true' };
    Object.entries(attributes).forEach(([key, value]) => svg.setAttribute(key, value));
    const path = document.createElementNS(svgNamespace, 'path');
    path.setAttribute('d', iconPaths[name]);
    if (name === 'pause') path.setAttribute('stroke-width', '3.5');
    svg.append(path);
    return svg;
  }

  function refreshPreviews() { previewUpdates.forEach(update => update()); }

  function pauseRecordings(except) {
    document.querySelectorAll('audio, video').forEach(media => {
      if (media !== except && !media.muted && !media.paused) media.pause();
    });
  }

  function bindDialog(dialog, onClose) {
    if (!dialog || boundDialogs.has(dialog)) return;
    boundDialogs.add(dialog);
    dialog.addEventListener('click', event => {
      if (event.target === dialog || event.target.closest('button[aria-label^="Close"]')) dialog.close();
    });
    dialog.addEventListener('close', () => {
      dialog.querySelectorAll('audio, video').forEach(media => media.pause());
      if (onClose) onClose();
      refreshPreviews();
      const opener = dialogOpeners.get(dialog);
      if (opener && opener.isConnected) opener.focus({ preventScroll: true });
    });
  }

  function openDialog(dialog, trigger) {
    if (!dialog) return;
    pauseRecordings();
    dialogOpeners.set(dialog, trigger);
    if (!dialog.open) dialog.showModal();
    refreshPreviews();
  }

  function closeButton(label) {
    const button = element('button', 'icon-button');
    button.type = 'button';
    button.setAttribute('aria-label', label);
    button.append(icon('close'));
    return button;
  }

  function waveform(recording, bars) {
    const peaks = Array.isArray(recording.peaks) ? recording.peaks : [];
    const svg = document.createElementNS(svgNamespace, 'svg');
    svg.setAttribute('class', bars ? 'ke-wave-bars' : 'ke-wave-fill');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('viewBox', bars ? '0 0 120 42' : '0 0 ' + Math.max(1, peaks.length - 1) + ' 32');
    if (bars) {
      peaks.filter((value, index) => index % 4 === 0).forEach((value, index) => {
        const path = document.createElementNS(svgNamespace, 'path');
        path.setAttribute('d', 'M' + (index * 4 + 2) + ' ' + (21 - value * 19) + 'v' + Math.max(1.3, value * 38));
        path.setAttribute('stroke', 'currentColor');
        path.setAttribute('stroke-width', '1.9');
        path.setAttribute('stroke-linecap', 'round');
        svg.append(path);
      });
    } else {
      svg.setAttribute('preserveAspectRatio', 'none');
      const points = peaks.map((value, index) => index + ',' + (16 - value * 15))
        .concat(peaks.map((value, index) => index + ',' + (16 + value * 15)).reverse());
      const shape = document.createElementNS(svgNamespace, 'path');
      shape.setAttribute('d', points.length ? 'M' + points.join(' L') + 'Z' : 'M0 16h1');
      shape.setAttribute('fill', 'currentColor');
      const line = document.createElementNS(svgNamespace, 'path');
      line.setAttribute('d', 'M0 16h' + peaks.length);
      line.setAttribute('stroke', 'currentColor');
      line.setAttribute('stroke-width', '.6');
      svg.append(shape, line);
    }
    return svg;
  }

  function bindAudio(container, initialRecording) {
    const audio = container.querySelector('audio');
    const button = container.querySelector('button');
    if (!audio || !button || !initialRecording) return null;
    const hero = container.classList.contains('ke-voice');
    const timeline = container.querySelector('.ke-timeline');
    const seek = timeline && timeline.querySelector('input');
    const labels = container.querySelectorAll(':scope > span');
    let recording = initialRecording;
    let duration = Number.isFinite(audio.duration) ? audio.duration : 0;
    let failed = false;
    let revision = 0;
    let lastPlaying = null;

    function render() {
      const playing = !audio.paused && !audio.ended;
      const position = playbackPosition(audio.currentTime, duration);
      if (playing !== lastPlaying) {
        const symbol = hero ? container.querySelector('.ke-voice-symbol') : button;
        if (symbol) symbol.replaceChildren(icon(playing ? 'pause' : 'play', 16));
        lastPlaying = playing;
      }
      if (hero) {
        const action = container.querySelector('.ke-voice-copy strong');
        const caption = container.querySelector('.ke-voice-copy small');
        if (action) action.textContent = playing ? 'Pause the message' : 'Hear the message';
        if (caption) caption.textContent = failed ? 'Audio unavailable' : '“' + recording.text + '”';
      } else {
        button.setAttribute('aria-label', playing ? 'Pause sample message' : 'Play sample message');
        if (labels[0]) labels[0].textContent = clock(position.time);
        if (labels[1]) labels[1].textContent = failed ? 'Unavailable' : '−' + clock(position.remaining);
        if (seek) {
          seek.max = String(position.duration || 1);
          seek.value = String(position.time);
          seek.disabled = !position.duration;
          seek.setAttribute('aria-valuetext', clock(position.time));
        }
        if (timeline) {
          let marker = timeline.querySelector('i');
          if (position.time > 0 && position.duration) {
            if (!marker) { marker = element('i'); marker.setAttribute('aria-hidden', 'true'); timeline.append(marker); }
            marker.style.left = position.progress + '%';
          } else if (marker) marker.remove();
        }
      }
    }

    function setRecording(next) {
      if (!next) return;
      revision++;
      audio.pause();
      recording = next;
      failed = false;
      duration = 0;
      const previousWave = container.querySelector('.ke-wave-fill, .ke-wave-bars');
      if (previousWave && Array.isArray(next.peaks)) previousWave.replaceWith(waveform(next, hero));
      container.dataset.recordingId = next.id;
      audio.src = next.src;
      audio.load();
      render();
    }

    button.addEventListener('click', async () => {
      if (!audio.paused) { audio.pause(); return; }
      const requestedRevision = revision;
      try {
        await audio.play();
        if (revision === requestedRevision) { failed = false; render(); }
      } catch (error) {
        if (revision === requestedRevision && error.name !== 'AbortError') { failed = true; render(); }
      }
    });
    ['play', 'pause', 'ended', 'timeupdate'].forEach(name => audio.addEventListener(name, render));
    ['loadedmetadata', 'durationchange'].forEach(name => audio.addEventListener(name, () => {
      duration = Number.isFinite(audio.duration) ? audio.duration : 0;
      render();
    }));
    audio.addEventListener('error', () => { failed = true; render(); });
    if (seek) seek.addEventListener('input', () => {
      if (duration > 0) audio.currentTime = playbackPosition(Number(seek.value), duration).time;
      render();
    });
    container.dataset.recordingId = recording.id;
    if (audio.getAttribute('src') !== recording.src) setRecording(recording);
    else render();
    return { setRecording };
  }

  function bindTrack(row) {
    let drag = null;
    let suppressClick = false;
    let glideFrame = 0;
    let glideTarget = null;
    const targets = () => trackStops(Array.from(row.children, child => child.offsetLeft), row.scrollWidth, row.clientWidth);
    const cancelGlide = () => { cancelAnimationFrame(glideFrame); glideFrame = 0; glideTarget = null; };
    function glideTo(left, animate) {
      cancelGlide();
      const start = row.scrollLeft;
      const distance = left - start;
      const complete = () => { row.scrollLeft = left; glideTarget = null; delete row.dataset.gliding; };
      if (!animate || motion.matches || Math.abs(distance) < 1) { complete(); return; }
      row.dataset.gliding = 'true';
      glideTarget = left;
      const duration = Math.min(480, 280 + Math.abs(distance) * .45);
      const started = performance.now();
      const step = now => {
        const progress = Math.min(1, (now - started) / duration);
        row.scrollLeft = start + distance * (1 - (1 - progress) ** 3);
        if (progress < 1) glideFrame = requestAnimationFrame(step);
        else { glideFrame = 0; complete(); }
      };
      glideFrame = requestAnimationFrame(step);
    }
    function settle(velocity, animate) {
      const carry = animate && !motion.matches ? Math.max(-140, Math.min(140, velocity * 150)) : 0;
      glideTo(trackDestination(targets(), row.scrollLeft + carry), animate);
    }
    function finishDrag(animate, releasedAt) {
      const completed = drag;
      drag = null;
      if (!completed) return;
      if (row.hasPointerCapture(completed.pointerId)) row.releasePointerCapture(completed.pointerId);
      const velocity = completed.active && releasedAt - completed.lastTime < 80 ? completed.velocity : 0;
      settle(velocity, completed.active && animate);
      delete row.dataset.dragPending;
      delete row.dataset.dragging;
    }
    function interruptMotion() { finishDrag(false, 0); if (row.dataset.gliding) settle(0, false); }
    row.addEventListener('pointerdown', event => {
      suppressClick = false;
      const target = event.target.closest('button, a, input, select, textarea, video[controls]');
      if (target && !target.hasAttribute('data-drag-play')) return;
      if (event.pointerType !== 'mouse' || event.button !== 0 || !event.isPrimary || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || row.scrollWidth <= row.clientWidth + 1) { interruptMotion(); return; }
      cancelGlide();
      row.dataset.dragPending = 'true';
      delete row.dataset.gliding;
      drag = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, scrollLeft: row.scrollLeft, active: false, lastLeft: row.scrollLeft, lastTime: event.timeStamp, velocity: 0 };
    });
    window.addEventListener('pointermove', event => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      if (!(event.buttons & 1)) { finishDrag(false, 0); return; }
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.active) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 6) return;
        if (Math.abs(dy) > Math.abs(dx)) { interruptMotion(); return; }
        drag.active = true;
        suppressClick = true;
        row.dataset.dragging = 'true';
        delete row.dataset.dragPending;
        row.setPointerCapture(event.pointerId);
      }
      event.preventDefault();
      row.scrollLeft = drag.scrollLeft - dx;
      const elapsed = event.timeStamp - drag.lastTime;
      if (elapsed > 0) {
        const speed = (row.scrollLeft - drag.lastLeft) / elapsed;
        drag.velocity = elapsed > 80 ? speed : drag.velocity * .35 + speed * .65;
        drag.lastLeft = row.scrollLeft;
        drag.lastTime = event.timeStamp;
      }
    }, { passive: false });
    const endDrag = event => { if (drag && event.pointerId === drag.pointerId) finishDrag(event.type === 'pointerup', event.timeStamp); };
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    row.addEventListener('lostpointercapture', endDrag);
    row.addEventListener('dragstart', event => { if (drag) event.preventDefault(); });
    row.addEventListener('wheel', interruptMotion, { passive: true });
    row.addEventListener('click', event => {
      if (suppressClick && event.detail > 0) { event.preventDefault(); event.stopPropagation(); suppressClick = false; }
    }, true);
    row.addEventListener('keydown', event => {
      if (event.target !== row || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) { interruptMotion(); return; }
      const destination = trackDestination(targets(), glideTarget ?? row.scrollLeft, event.key);
      event.preventDefault();
      finishDrag(false, 0);
      glideTo(destination, true);
    });
    const updateSize = () => { interruptMotion(); row.dataset.draggable = String(row.scrollWidth > row.clientWidth + 1); };
    if (window.ResizeObserver) new ResizeObserver(updateSize).observe(row);
    else window.addEventListener('resize', updateSize);
    window.addEventListener('blur', interruptMotion);
    document.addEventListener('visibilitychange', () => { if (document.hidden) interruptMotion(); });
    motion.addEventListener('change', interruptMotion);
    updateSize();
  }

  function bindVideos(container, containerIndex) {
    const dialog = container.querySelector('.bottle-video-dialog');
    const track = container.querySelector('.bottle-videos__track');
    if (!dialog || !track) return;
    bindTrack(track);
    bindDialog(dialog, () => { dialog.replaceChildren(); });
    const titleId = 'bottle-video-title-' + containerIndex;
    dialog.setAttribute('aria-labelledby', titleId);
    const states = new Map();
    function updateTile(state) {
      const active = shouldPreview({ visible: state.visible, hidden: document.hidden, reducedMotion: motion.matches, saveData: connection && connection.saveData, dialogOpen: Boolean(document.querySelector('dialog[open]')), failed: state.failed });
      if (state.active !== active) state.revision++;
      state.active = active;
      if (!active) {
        if (state.video) state.video.pause();
        state.poster.classList.remove('is-playing');
        return;
      }
      if (!state.video) {
        const video = element('video');
        state.video = video;
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
        video.preload = 'none';
        video.tabIndex = -1;
        video.setAttribute('aria-hidden', 'true');
        video.addEventListener('playing', () => {
          if (state.active) state.poster.classList.add('is-playing');
          else video.pause();
        });
        video.addEventListener('error', () => { state.failed = true; updateTile(state); });
        video.src = state.clip.previewUrl;
        state.picture.insertBefore(video, state.button);
      }
      if (state.video.paused && !state.pending) {
        state.pending = true;
        const requestedRevision = state.revision;
        const requested = state.video.play();
        Promise.resolve(requested).catch(error => {
          if (state.active && requestedRevision === state.revision && error.name !== 'AbortError') state.failed = true;
        }).finally(() => {
          state.pending = false;
          if (state.failed || !state.active) { state.video.pause(); state.poster.classList.remove('is-playing'); }
          else if (state.video.paused && requestedRevision !== state.revision) updateTile(state);
        });
      }
    }
    const observer = window.IntersectionObserver ? new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const state = states.get(entry.target);
        state.visible = entry.isIntersecting && entry.intersectionRatio >= .2;
        updateTile(state);
      });
    }, { threshold: [0, .2] }) : null;
    track.querySelectorAll('.bottle-video').forEach((tile, index) => {
      const clip = recordingAt(config.videos, index);
      const poster = tile.querySelector('img');
      const button = tile.querySelector('.bottle-video__play');
      const picture = tile.querySelector('.bottle-video__picture');
      if (!clip || !poster || !button || !picture) return;
      const state = { clip, poster, button, picture, visible: false, failed: false, active: false, pending: false, revision: 0, video: null };
      states.set(tile, state);
      if (observer) observer.observe(tile);
      button.addEventListener('click', () => {
        const content = element('div', 'bottle-video-dialog__content');
        const header = element('div', 'bottle-video-dialog__header');
        const title = element('h2', '', clip.title);
        title.id = titleId;
        header.append(title, closeButton('Close video'));
        const player = element('video');
        player.src = clip.videoUrl;
        player.poster = clip.posterUrl;
        player.controls = true;
        player.playsInline = true;
        player.preload = 'metadata';
        player.setAttribute('aria-label', clip.title + ' by ' + clip.creator);
        player.addEventListener('error', () => {
          player.pause();
          const error = element('p', 'bottle-video-error', 'This video could not load. Close it and try again.');
          error.setAttribute('role', 'alert');
          player.replaceWith(error);
        }, { once: true });
        content.append(header, player);
        dialog.replaceChildren(content);
        openDialog(dialog, button);
        player.play().catch(() => { /* Native controls remain available when autoplay is denied. */ });
      });
    });
    const refresh = () => states.forEach(state => updateTile(state));
    previewUpdates.push(refresh);
    if (!observer) {
      const updateVisible = () => {
        states.forEach((state, tile) => {
          const rect = tile.getBoundingClientRect();
          const row = track.getBoundingClientRect();
          const visibleWidth = Math.max(0, Math.min(rect.right, row.right, innerWidth) - Math.max(rect.left, row.left, 0));
          const visibleHeight = Math.max(0, Math.min(rect.bottom, innerHeight) - Math.max(rect.top, 0));
          state.visible = rect.width > 0 && rect.height > 0 && visibleWidth * visibleHeight / (rect.width * rect.height) >= .2;
        });
        refresh();
      };
      document.addEventListener('scroll', updateVisible, { passive: true, capture: true });
      window.addEventListener('resize', updateVisible);
      updateVisible();
    }
  }

  function bindReviews(container, containerIndex) {
    const dialog = container.querySelector('.bottle-reviews__dialog');
    if (!dialog) return;
    bindDialog(dialog, () => { dialog.replaceChildren(); });
    container.querySelectorAll('.bottle-reviews__photo').forEach((button, index) => {
      const figure = button.closest('figure');
      const photo = button.querySelector('img');
      const byline = figure && figure.querySelector('small');
      if (!figure || !photo) return;
      if (byline && !byline.id) byline.id = 'review-byline-' + containerIndex + '-' + index;
      button.addEventListener('click', () => {
        const photoId = figure.id.replace(/^review-/, '');
        const originalPath = '/images/reviews/' + photoId + '-full.webp';
        const full = element('img');
        full.src = config.images && config.images[originalPath] || photo.currentSrc || photo.src;
        full.alt = photo.alt;
        full.width = Number(photo.getAttribute('width')) || photo.naturalWidth;
        full.height = Number(photo.getAttribute('height')) || photo.naturalHeight;
        const content = element('div');
        content.append(closeButton('Close photo'), full);
        dialog.replaceChildren(content);
        if (byline) dialog.setAttribute('aria-describedby', byline.id);
        openDialog(dialog, button);
      });
    });
  }

  function initialize() {
    document.addEventListener('play', event => {
      const current = event.target;
      if (current instanceof HTMLMediaElement && !current.muted) pauseRecordings(current);
    }, true);
    document.querySelectorAll('.ke-voice, .ke-player').forEach(container => {
      const isHero = container.classList.contains('ke-voice');
      const audio = container.querySelector('audio');
      const id = container.dataset.recordingId || audio && audio.dataset.recordingId;
      const recording = isHero ? config.featuredRecording : (config.samples || []).find(sample => sample.id === id || audio && (audio.getAttribute('src') || '').includes(sample.id)) || recordingAt(config.samples, 0);
      const controller = bindAudio(container, recording);
      if (controller) audioPlayers.set(container, controller);
    });
    document.querySelectorAll('.ke-examples').forEach(section => {
      const buttons = Array.from(section.querySelectorAll('.ke-tabs button'));
      const quote = section.querySelector('.ke-letter blockquote');
      const player = audioPlayers.get(section.querySelector('.ke-player'));
      let active = Math.max(0, buttons.findIndex(button => button.getAttribute('aria-pressed') === 'true'));
      buttons.forEach((button, index) => button.addEventListener('click', () => {
        const recording = recordingAt(config.samples, index);
        if (!recording || index === active) return;
        active = index;
        buttons.forEach((tab, tabIndex) => tab.setAttribute('aria-pressed', String(tabIndex === index)));
        if (quote) quote.textContent = '“' + recording.text + '”';
        if (player) player.setRecording(recording);
      }));
    });
    document.querySelectorAll('.bottle-videos').forEach(bindVideos);
    document.querySelectorAll('.bottle-reviews').forEach(bindReviews);
    const story = document.querySelector('.ke-story-dialog');
    if (story) {
      bindDialog(story);
      document.querySelectorAll('.ke-story-link').forEach(button => button.addEventListener('click', () => openDialog(story, button)));
    }
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) document.querySelectorAll('audio, video').forEach(media => media.pause());
      refreshPreviews();
    });
    window.addEventListener('pagehide', () => document.querySelectorAll('audio, video').forEach(media => media.pause()));
    window.addEventListener('pageshow', refreshPreviews);
    motion.addEventListener('change', refreshPreviews);
    if (connection && connection.addEventListener) connection.addEventListener('change', refreshPreviews);
    new MutationObserver(refreshPreviews).observe(document.body, { attributes: true, attributeFilter: ['open'], subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
